import { useCallback, useEffect, useMemo, useState } from "react";
import { apiRequest } from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import AttendanceDayEditorModal from "./components/AttendanceDayEditorModal";
import HrmsFilters from '../../components/hrms/HrmsFilterPanel';
import { matchesHrmsFilters } from '../../components/hrms/hrmsFilterUtils';

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const STATUS_META = {
  P: {
    label: "Present",
    tileClass: "bg-green-50 text-green-800 border border-green-200",
    dotClass: "bg-green-500",
  },
  A: {
    label: "Absent",
    tileClass: "bg-red-50 text-red-900 border border-red-200",
    dotClass: "bg-red-800",
  },
  L: {
    label: "Leave",
    tileClass: "bg-blue-50 text-blue-800 border border-blue-200",
    dotClass: "bg-blue-500",
  },
  LT: {
    label: "Late",
    tileClass: "bg-amber-50 text-amber-800 border border-amber-200",
    dotClass: "bg-amber-500",
  },
  WO: {
    label: "Week off",
    tileClass: "bg-gray-100 text-gray-400 border border-gray-200",
    dotClass: "bg-gray-400",
  },
  NONE: {
    label: "No record",
    tileClass: "bg-gray-50 text-gray-300 border border-gray-100",
    dotClass: "bg-gray-300",
  },
};

const STAT_CARDS = [
  {
    key: "present",
    label: "Present",
    accentClass: "border-t-green-500",
    iconClass: "text-green-600",
    icon: <CheckIcon />,
  },
  {
    key: "absent",
    label: "Absent",
    accentClass: "border-t-red-800",
    iconClass: "text-red-800",
    icon: <XIcon />,
  },
  {
    key: "leave",
    label: "Leave",
    accentClass: "border-t-blue-500",
    iconClass: "text-blue-600",
    icon: <PlaneIcon />,
  },
  {
    key: "late",
    label: "Late",
    accentClass: "border-t-amber-500",
    iconClass: "text-amber-600",
    icon: <ClockIcon />,
  },
];

function indiaMonth() {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
  }).formatToParts(new Date());

  const year = parts.find((part) => part.type === "year")?.value;
  const month = parts.find((part) => part.type === "month")?.value;

  return `${year}-${month}`;
}

function indiaTodayKey() {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());

  const year = parts.find((part) => part.type === "year")?.value;
  const month = parts.find((part) => part.type === "month")?.value;
  const day = parts.find((part) => part.type === "day")?.value;

  return `${year}-${month}-${day}`;
}

function mapAttendanceStatus(status) {
  const statuses = {
    PRESENT: "P",
    ABSENT: "A",
    ON_LEAVE: "L",
    LATE: "LT",
    WEEKLY_OFF: "WO",
  };

  return statuses[status] || "NONE";
}

export default function MonthlyAttendance() {
  const { accessToken } = useAuth();

  const [employees, setEmployees] = useState([]);
  const [hrmsFilters, setHrmsFilters] = useState({});
  const filteredEmployees = useMemo(() => employees.filter(row => matchesHrmsFilters(row,hrmsFilters)), [employees,hrmsFilters]);
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(indiaMonth());
  const [monthlyRecords, setMonthlyRecords] = useState([]);
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(true);
  const [isLoadingAttendance, setIsLoadingAttendance] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => { if (!filteredEmployees.some(row => row.id === selectedEmployee)) setSelectedEmployee(filteredEmployees[0]?.id || ''); }, [filteredEmployees,selectedEmployee]);

  const loadEmployees = useCallback(async () => {
    if (!accessToken) return;

    setIsLoadingEmployees(true);
    setError("");

    try {
      const response = await apiRequest("/attendance/admin/employees", {
        accessToken,
      });

      const rawEmployees = Array.isArray(response)
        ? response
        : response?.employees || response?.data || [];

      const employeeList = rawEmployees
        .map((employee) => ({
          ...employee,
          id: employee.id || employee.employeeId,
          employeeCode: employee.employeeCode,
          fullName: employee.fullName,
          designation:
            employee.designation ||
            employee.assignments?.[0]?.designation?.name ||
            null,
          workSchedule: employee.workSchedule || null,
        }))
        .filter((employee) => employee.id);

      setEmployees(employeeList);

      setSelectedEmployee((currentEmployeeId) => {
        const exists = employeeList.some(
          (employee) => employee.id === currentEmployeeId,
        );

        return exists ? currentEmployeeId : employeeList[0]?.id || "";
      });
    } catch (requestError) {
      setEmployees([]);
      setSelectedEmployee("");
      setError(requestError.message || "Unable to load employees.");
    } finally {
      setIsLoadingEmployees(false);
    }
  }, [accessToken]);

  const loadMonthlyAttendance = useCallback(async () => {
    if (!accessToken || !selectedEmployee) {
      setMonthlyRecords([]);
      return;
    }

    setIsLoadingAttendance(true);
    setError("");

    try {
      const response = await apiRequest(
        `/attendance/admin/monthly?employeeId=${selectedEmployee}&month=${selectedMonth}`,
        { accessToken },
      );

      setMonthlyRecords(response?.records || []);
    } catch (requestError) {
      setMonthlyRecords([]);
      setError(requestError.message || "Unable to load monthly attendance.");
    } finally {
      setIsLoadingAttendance(false);
    }
  }, [accessToken, selectedEmployee, selectedMonth]);

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  useEffect(() => {
    loadMonthlyAttendance();
  }, [loadMonthlyAttendance]);

  const calendarDays = useMemo(() => {
    const [year, month] = selectedMonth.split("-").map(Number);
    const totalDays = new Date(year, month, 0).getDate();
    const today = indiaTodayKey();

    const attendanceByDate = new Map(
      monthlyRecords.map((record) => [record.date, record]),
    );

    return Array.from({ length: totalDays }, (_, index) => {
      const dayNumber = index + 1;
      const date = `${selectedMonth}-${String(dayNumber).padStart(2, "0")}`;
      const record = attendanceByDate.get(date);

      return {
        dayNumber,
        date,
        record: record || null,
        status: record
          ? mapAttendanceStatus(record.status)
          : date < today
            ? "A"
            : "NONE",
      };
    });
  }, [monthlyRecords, selectedMonth]);

  const firstDayBlankTiles = useMemo(() => {
    const [year, month] = selectedMonth.split("-").map(Number);
    const sundayBasedDay = new Date(year, month - 1, 1).getDay();

    // Converts Sunday=0 to Monday=0.
    return (sundayBasedDay + 6) % 7;
  }, [selectedMonth]);

  const stats = useMemo(() => {
    const statuses = calendarDays.map((day) => day.status);

    return {
      present: statuses.filter((status) => status === "P").length,
      absent: statuses.filter((status) => status === "A").length,
      leave: statuses.filter((status) => status === "L").length,
      late: statuses.filter((status) => status === "LT").length,
    };
  }, [calendarDays]);

  const recordedWorkDays =
    stats.present + stats.absent + stats.leave + stats.late;

  const getStyles = (status) =>
    STATUS_META[status]?.tileClass || STATUS_META.NONE.tileClass;

  const selectedEmployeeData = employees.find(
    (employee) => employee.id === selectedEmployee,
  );

  return (
    <div className="space-y-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Monthly Attendance</h1>

        <p className="mt-2 text-gray-500">
          View attendance trends for employees.
        </p>
      </div>

      <HrmsFilters records={employees} value={hrmsFilters} onChange={setHrmsFilters} />
      <div className="bg-white rounded-2xl border border-gray-100 p-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-widest text-black mb-1.5">
              Employee
            </label>

            <div className="relative">
              <select
                value={selectedEmployee}
                disabled={isLoadingEmployees || !filteredEmployees.length}
                onChange={(event) => setSelectedEmployee(event.target.value)}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 text-sm text-gray-800 font-medium appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-800 focus:border-transparent transition disabled:cursor-not-allowed disabled:opacity-60"
              >
                {!filteredEmployees.length ? (
                  <option value="">No employees available</option>
                ) : (
                  filteredEmployees.map((employee) => (
                    <option key={employee.id} value={employee.id}>
                      {employee.fullName} · {employee.employeeCode}
                    </option>
                  ))
                )}
              </select>

              <ChevronDownIcon className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>

            {selectedEmployeeData?.designation ? (
              <p className="mt-2 text-xs text-gray-500">
                {selectedEmployeeData.designation}
              </p>
            ) : null}
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-widest text-black mb-1.5">
              Month
            </label>

            <input
              type="month"
              value={selectedMonth}
              onChange={(event) => setSelectedMonth(event.target.value)}
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 text-sm text-gray-800 font-medium focus:outline-none focus:ring-2 focus:ring-primary-800 focus:border-transparent transition cursor-pointer"
            />
          </div>
        </div>
      </div>

      {error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {STAT_CARDS.map(({ key, label, accentClass, iconClass, icon }) => (
          <div
            key={key}
            className={`bg-white rounded-br-2xl rounded-bl-2xl border border-gray-100 border-t-2 p-5 ${accentClass}`}
          >
            <div
              className={`flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest mb-3 ${iconClass}`}
            >
              {icon}
              {label}
            </div>

            <p className="text-3xl font-semibold text-gray-900 tracking-tight">
              {stats[key]}
            </p>

            <p className="text-xs text-gray-400 mt-1">days this month</p>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 p-5">
        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <GridIcon className="w-4 h-4 text-primary-900" />

            <h2 className="text-lg font-semibold text-gray-900">
              Attendance Calendar
            </h2>
          </div>

          <div className="flex items-center gap-4 flex-wrap">
            {Object.entries(STATUS_META)
              .filter(([key]) => key !== "NONE")
              .map(([key, meta]) => (
                <div key={key} className="flex items-center gap-1.5">
                  <span className={`w-3 h-3 rounded-full ${meta.dotClass}`} />

                  <span className="text-sm text-gray-500">{meta.label}</span>
                </div>
              ))}
          </div>
        </div>

        {isLoadingAttendance ? (
          <div className="min-h-72 flex items-center justify-center">
            <div className="h-10 w-10 rounded-full border-4 border-primary-100 border-t-primary-800 animate-spin" />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-7 gap-1.5 mb-3">
              {DAY_LABELS.map((day) => (
                <div
                  key={day}
                  className="text-center text-xs font-semibold uppercase tracking-widest text-gray-500"
                >
                  {day}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-1.5">
              {Array.from({ length: firstDayBlankTiles }).map((_, index) => (
                <div key={`blank-${index}`} />
              ))}

              {calendarDays.map(({ dayNumber, date, status, record }) => {
                const isFuture = date > indiaTodayKey();

                return (
                  <button
                    key={dayNumber}
                    type="button"
                    disabled={isFuture}
                    onClick={() =>
                      setSelectedDay({
                        dayNumber,
                        date,
                        status,
                        record,
                      })
                    }
                    title={`Day ${dayNumber} — ${
                      STATUS_META[status]?.label || "No record"
                    }`}
                    className={`h-12 rounded-xl flex flex-col items-center justify-center gap-1 transition-all duration-150 hover:scale-105 hover:shadow-sm disabled:cursor-not-allowed disabled:opacity-45 ${getStyles(
                      status,
                    )}`}
                  >
                    <span className="text-[11px] font-medium opacity-60 leading-none">
                      {dayNumber}
                    </span>

                    <span className="text-sm font-bold leading-none tracking-tight">
                      {status === "NONE" ? "—" : status}
                    </span>
                  </button>
                );
              })}
            </div>
          </>
        )}

        <div className="border-t border-gray-100 mt-5 pt-5 space-y-3">
          {[
            {
              label: "Present",
              count: stats.present,
              barClass: "bg-green-500",
            },
            {
              label: "Leave",
              count: stats.leave,
              barClass: "bg-blue-500",
            },
            {
              label: "Late",
              count: stats.late,
              barClass: "bg-amber-400",
            },
            {
              label: "Absent",
              count: stats.absent,
              barClass: "bg-red-800",
            },
          ].map(({ label, count, barClass }) => {
            const percentage =
              recordedWorkDays > 0
                ? Math.round((count / recordedWorkDays) * 100)
                : 0;

            return (
              <div key={label}>
                <div className="flex justify-between text-sm text-gray-500 mb-1">
                  <span className="font-medium text-gray-600">{label}</span>

                  <span>
                    {count} days · {percentage}%
                  </span>
                </div>

                <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                  <div
                    className={`h-full rounded-full ${barClass} transition-all duration-500`}
                    style={{ width: `${percentage}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <AttendanceDayEditorModal
        open={Boolean(selectedDay)}
        selectedDay={selectedDay}
        employee={{
          ...selectedEmployeeData,
          accessToken,
        }}
        onClose={() => setSelectedDay(null)}
        onSaved={loadMonthlyAttendance}
      />
    </div>
  );
}

function GridIcon({ className }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  );
}

function ChevronDownIcon({ className }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      className="w-3 h-3"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg
      className="w-3 h-3"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function PlaneIcon() {
  return (
    <svg
      className="w-3 h-3"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M22 2L11 13" />
      <path d="M22 2L15 22L11 13L2 9L22 2z" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg
      className="w-3 h-3"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}
